import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const prevTitle = document.title;
    document.title = 'Terms of Service | Hoosier Life Insurance';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setCanonical = (href: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', 'Terms of Service for Hoosier Life Insurance. Review our terms, SMS consent, TCPA compliance & service agreement for Indiana insurance clients.');
    setMeta('keywords', 'terms of service, SMS terms, TCPA compliance, insurance terms, service agreement, Indianapolis life insurance');
    setMeta('robots', 'index, follow');
    setCanonical(`${SITE_URL}/terms-of-service`);
    setMeta('og:title', 'Terms of Service | Hoosier Life Insurance', true);
    setMeta('og:description', 'Terms of Service for Hoosier Life Insurance. Review our terms, SMS consent requirements, and service agreement.', true);
    setMeta('og:url', `${SITE_URL}/terms-of-service`, true);
    setMeta('twitter:title', 'Terms of Service | Hoosier Life Insurance');
    setMeta('twitter:description', 'Read the Hoosier Life Insurance terms of service. Understand the terms governing use of our website and services.');
    setMeta('last-modified', new Date().toISOString());

    const schemaData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service',
      description: 'Terms of Service for Hoosier Life Insurance. Review our terms, SMS consent requirements, TCPA compliance, and service agreement.',
      url: `${SITE_URL}/terms-of-service`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${SITE_URL}/terms-of-service` },
        ],
      },
    };

    let schemaEl = document.getElementById('schema-terms-of-service');
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = 'schema-terms-of-service';
      (schemaEl as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(schemaData);

    return () => {
      document.title = prevTitle;
      document.getElementById('schema-terms-of-service')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#1e3a5f] via-[#2d5a8c] to-[#1e3a5f] text-white py-6 sticky top-0 z-50 shadow-md">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-shield-check-line text-3xl text-[#D97642]"></i>
            </div>
            <span className="text-xl font-bold">Hoosier Life Insurance</span>
          </Link>
          <Link to="/" className="text-sm hover:text-[#D97642] transition-colors flex items-center gap-2">
            <i className="ri-home-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Page Title Banner */}
      <div className="bg-[#F7F9FB] border-b border-gray-200 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-2">Terms of Service</h1>
          <p className="text-gray-500 text-sm">Last Updated: January 14, 2025</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-10 text-gray-700 leading-relaxed">

          {/* 1. Acceptance */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              Welcome to Hoosier Life Insurance. By accessing or using our website, services, or submitting any information through our forms, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and Hoosier Life Insurance. We reserve the right to modify these Terms at any time, and your continued use of our services after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* 2. Services */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">2. Description of Services</h2>
            <p className="mb-4">
              Hoosier Life Insurance provides insurance quote comparison, consultation scheduling, and educational services related to life insurance, annuities, and related financial products in Central Indiana. Our services include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Online quote request forms and calculators</li>
              <li>Appointment scheduling and consultation services</li>
              <li>Educational content about insurance products</li>
              <li>SMS and email communications regarding insurance options</li>
              <li>Connection to licensed insurance agents and carriers</li>
            </ul>
            <p>
              We act as an intermediary to connect consumers with appropriate insurance solutions and licensed professionals. We do not directly underwrite or issue insurance policies.
            </p>
          </section>

          {/* 3. SMS Terms — Highlighted */}
          <section id="sms" className="bg-orange-50 border-l-4 border-[#D97642] p-6 rounded-r-lg">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">3. SMS &amp; Text Message Terms</h2>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3">3.1 Consent to Receive Text Messages</h3>
            <p className="mb-4">
              By providing your mobile phone number and checking the SMS consent checkbox on any form on this website, you expressly consent to receive automated marketing text messages, appointment reminders, quote information, and service updates at the phone number you provided.
            </p>
            <p className="mb-4">
              <strong>Your consent is not a condition of purchase.</strong> You may still receive our services without agreeing to receive text messages.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.2 Message Frequency</h3>
            <p className="mb-4">
              Message frequency varies depending on your interaction with our services. You may receive up to <strong>10 messages per month</strong>, including appointment confirmations, quote follow-ups, and promotional offers.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.3 Message and Data Rates</h3>
            <p className="mb-4">
              <strong>Message and data rates may apply.</strong> Standard text messaging rates from your wireless carrier will apply to all messages sent and received.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.4 How to Opt-Out</h3>
            <p className="mb-3">You may opt-out of receiving text messages at any time by replying with any of the following keywords:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>STOP</strong></li>
              <li><strong>END</strong></li>
              <li><strong>CANCEL</strong></li>
              <li><strong>UNSUBSCRIBE</strong></li>
              <li><strong>QUIT</strong></li>
            </ul>
            <p className="mb-4">
              After you send an opt-out message, you will receive one final confirmation message. You will not receive any further messages unless you re-opt-in.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.5 Help and Support</h3>
            <p className="mb-4">
              For help, reply <strong>HELP</strong> to any text message, or contact us at:
            </p>
            <ul className="list-none space-y-1 mb-4">
              <li><strong>Phone:</strong> <a href="tel:+13176482983" className="text-[#D97642] hover:underline">(317) 648-2983</a></li>
              <li><strong>Email:</strong> <a href="mailto:info@hoosierlifeinsurance.com" className="text-[#D97642] hover:underline">info@hoosierlifeinsurance.com</a></li>
            </ul>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.6 Carrier Disclaimer</h3>
            <p className="mb-4">
              Carriers are not liable for delayed or undelivered messages. Supported carriers include AT&amp;T, Verizon Wireless, T-Mobile, Sprint, and other major U.S. carriers.
            </p>

            <h3 className="text-xl font-semibold text-[#1e3a5f] mb-3 mt-6">3.7 TCPA Compliance</h3>
            <p>
              We comply with the Telephone Consumer Protection Act (TCPA) and all applicable federal and state regulations regarding automated text messaging. We will only send messages to numbers for which we have received express written consent.
            </p>
          </section>

          {/* 4. No Insurance Advice */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">4. No Insurance Advice Disclaimer</h2>
            <p className="mb-4">
              <strong>The information provided on this website is for general informational and educational purposes only.</strong> It does not constitute professional insurance advice, financial planning advice, or a recommendation to purchase any specific insurance product.
            </p>
            <p className="mb-4">Before making any insurance decisions, you should:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Consult with a licensed insurance professional</li>
              <li>Review all policy documents and terms carefully</li>
              <li>Consider your specific financial situation and needs</li>
              <li>Seek independent legal or financial advice if necessary</li>
            </ul>
          </section>

          {/* 5. Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">5. Limitation of Liability</h2>
            <p className="mb-4">
              To the fullest extent permitted by law, Hoosier Life Insurance, its officers, directors, employees, agents, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, loss of data, business interruption, or personal injury.
            </p>
            <p>
              Our total liability for any claims arising from your use of our services shall not exceed the amount you paid to us, if any, in the twelve (12) months preceding the claim.
            </p>
          </section>

          {/* 6. Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">6. Intellectual Property</h2>
            <p className="mb-4">
              All content on this website, including text, graphics, logos, images, and design elements, is the property of Hoosier Life Insurance or its content suppliers and is protected by applicable intellectual property laws.
            </p>
            <p>
              You may not reproduce, distribute, or modify any content from this website without our express written permission.
            </p>
          </section>

          {/* 7. Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">7. Third-Party Links and Services</h2>
            <p className="mb-4">
              Our website may contain links to third-party websites. We have no control over, and assume no responsibility for, the content or practices of any third-party websites. We do not endorse any third-party content.
            </p>
          </section>

          {/* 8. User Conduct */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">8. User Conduct &amp; Prohibited Activities</h2>
            <p className="mb-4">You agree not to use our services for any unlawful purpose. Prohibited activities include:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Providing false or misleading information</li>
              <li>Impersonating any person or entity</li>
              <li>Attempting to gain unauthorized access to our systems</li>
              <li>Transmitting viruses or other harmful code</li>
              <li>Violating any applicable laws or regulations</li>
            </ul>
          </section>

          {/* 9. Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">9. Governing Law and Jurisdiction</h2>
            <p className="mb-4">
              These Terms shall be governed by the laws of the State of Indiana, United States. Any legal action shall be brought exclusively in the state or federal courts located in Marion County, Indiana.
            </p>
          </section>

          {/* 10. Changes */}
          <section>
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">10. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by updating the "Last Updated" date on this page. Your continued use of our services after any changes constitutes acceptance of those changes.
            </p>
          </section>

          {/* 11. Contact */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">11. Contact Information</h2>
            <p className="mb-4">If you have questions about these Terms, please contact us:</p>
            <div className="space-y-1">
              <p><strong>Hoosier Life Insurance</strong></p>
              <p>5625 German Church Road, Indianapolis, IN 46235</p>
              <p><strong>Phone:</strong> <a href="tel:+13176482983" className="text-[#D97642] hover:underline">(317) 648-2983</a></p>
              <p><strong>Email:</strong> <a href="mailto:info@hoosierlifeinsurance.com" className="text-[#D97642] hover:underline">info@hoosierlifeinsurance.com</a></p>
              <p><strong>Hours:</strong> Mon–Fri 9AM–5PM · Sat 8AM–1PM EST</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-[#F7F9FB] border border-gray-200 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-3">Acknowledgment</h2>
            <p>
              <strong>By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
            </p>
          </section>

        </div>

        {/* Back to Top + Home */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#D97642] transition-colors font-medium">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#D97642] transition-colors font-medium cursor-pointer"
          >
            <i className="ri-arrow-up-line"></i>
            Back to Top
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#1e3a5f] via-[#2d5a8c] to-[#1e3a5f] text-white py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-300">
            © {new Date().getFullYear()} Hoosier Life Insurance. All rights reserved.
          </p>
          <div className="mt-4 flex items-center justify-center gap-6 text-sm">
            <Link to="/privacy-policy" className="text-gray-300 hover:text-[#D97642] transition-colors">Privacy Policy</Link>
            <span className="text-gray-500">|</span>
            <Link to="/terms-of-service" className="text-gray-300 hover:text-[#D97642] transition-colors">Terms of Service</Link>
            <span className="text-gray-500">|</span>
            <a href="/#contact" className="text-gray-300 hover:text-[#D97642] transition-colors">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
